import axios from 'axios';
import qs from 'qs';
import { runAndHandleInvalidAuth } from '@/helpers/api/auth';
import useDataSourceStore from '@/stores/dataSource';
import useUserStore from '@/stores/user';
import router, { routeInProgress } from '@/router';
import { joinPaths } from '@/helpers/utils';
import { RECORD_NOT_FOUND_ERROR, isRedirectedRequestError, sessionExpiredError } from '@/helpers/api/redirectedRequestError';

axios.defaults.withCredentials = true;

export interface RequestMoreOptions extends RequestSideEffectsMoreOptions {
  baseUrl?: string,
}

interface RequestSideEffectsMoreOptions {
  callbackAfterResponse?: Function,
  isLogoutOnInvalidAuth?: boolean,
  config?: object
}

export default function useApi() {
  const { activeDataSourceBaseUrl } = useDataSourceStore();
  const baseUrlDefault = activeDataSourceBaseUrl || "";

  async function postUrlEncoded(
    path: string,
    data: object,
    moreOptions?: RequestMoreOptions
  ) {
    const { responseBody, response } = await fetchResponseBody({
      method: 'POST',
      path,
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      data: qs.stringify(data),
    }, moreOptions);

    return { responseBody, response };
  }

  async function post(
    path: string,
    data: object,
    moreOptions?: RequestMoreOptions
  ) {
    const { responseBody, response } = await fetchResponseBody({
      method: 'POST',
      path,
      data,
    }, moreOptions);

    return { responseBody, response };
  }

  async function put(
    path: string,
    data: object,
    moreOptions?: RequestMoreOptions
  ) {
    const { responseBody, response } = await fetchResponseBody({
      method: 'PUT',
      path,
      data,
    }, moreOptions);

    return { responseBody, response };
  }

  async function get(
    path: string,
    moreOptions?: RequestMoreOptions & { params?: Record<string, any> } 
  ) {
    const { params, ...otherOptions } = moreOptions || {};
    if (params) {
      const queryString = new URLSearchParams(params).toString();
      path += `?${queryString}`; 
    }
    const { responseBody, response } = await fetchResponseBody(
      {
        method: 'GET',
        path,
      },
      otherOptions
    );
    return { responseBody, response };
  }

  // TODO (cleaner code): think about a more consistent name for "deleteRequest" function as we can't name it "delete" [DSP-N26EJE9H]
  // TODO (cleaner code): think if there's a better way to resolve redundancy between "deleteRequest", "get", "post" [DSP-N26EKU8b]
  async function deleteRequest(
    path: string,
    moreOptions?: RequestMoreOptions,
  ) {
    const { responseBody, response } = await fetchResponseBody({
      method: 'DELETE',
      path,
    }, moreOptions);
    return { responseBody, response };
  }
  
  async function fetchResponseBody(
    {
      method,
      path,
      ...restOfOptions
    }: FetchResponseBodyOptions,
    {   // more options
      baseUrl = baseUrlDefault,
      config = {},   // config to be passed to requester function (e.g. axios)
      ...restOfMoreOptions
    }: RequestMoreOptions = {}
  ) {
    const url = new URL( joinPaths(baseUrl,path) ).toString();
    const options = {
      url,
      ...config,
      ...restOfOptions,
      // Last: config must not override it. Method decides whether a failure may
      // navigate away.
      method: method.toUpperCase(),
    };
    const { responseBody, response } = await fetchReposneWithSideEffects(options, restOfMoreOptions);
    return { responseBody, response };
  }

  return {
    postUrlEncoded,
    post,
    put,
    get,
    deleteRequest,
    fetchResponseBody
  }
}


// Side utlity functions
export function getModelFromViewCode(viewBodyText: string) {
  const regex = /(?<=model = )[^;]*/g;
  const dataStr = String( viewBodyText.match(regex) );
  const dataObj = JSON.parse(dataStr);
  return dataObj;
}

/**
 * 'media/edit/:id' and 'users/edit/:id' answer 200 with the whole AngularJS page
 * either way, so a bad id has no status to catch it by. Only `entities` decides:
 * an unreadable id still gets a full model, just with empty search boxes in it.
 */
function viewCodeHasRecord(viewBodyText: string) {
  try {
    const model = getModelFromViewCode(viewBodyText);
    return Array.isArray(model?.entities) && model.entities.length > 0;
  } catch {
    return false;   // unparseable = unrenderable
  }
}



// Side helpers

interface FetchResponseBodyOptions {
  method: string,
  path: string,
  headers?: object,
  data?: object | string,
}


async function fetchReposneWithSideEffects(
  options,
  {
    callbackAfterResponse,
    isLogoutOnInvalidAuth,
  }: RequestSideEffectsMoreOptions) {
  // Decided once, when the request starts: by the time it answers the user may have navigated on.
  const isPrimaryResource = isPrimaryResourceRequest(recordLoadUrlOf(options));
  try {
    // fetch reponse, and if isLogoutOnInvalidAuth is true, then handle logging out user on invalid auth side effect.
    const response = await fetchResponseAndHandleAuth(options, {
      isLogoutOnInvalidAuth,
      isPrimaryResource,
    });

    // The auth check took over (logged out, or went to not-found). Stop the caller
    // here rather than hand it an empty result to unwrap.
    if (response === undefined) throw sessionExpiredError();

    // if callbackAfterResponse side effect is defined, call it
    if(typeof callbackAfterResponse == 'function') {
      callbackAfterResponse(response);
    }

    const responseBody = response?.data;
    assertRecordFound(responseBody, isPrimaryResource);
    return { responseBody, response };
  } catch(err: any) {
    // Bugsnag reports every console.error; a redirect is not an error.
    if (!isRedirectedRequestError(err)) console.error("Error fetching request with side effects:", err);
    await handleResponseStatusError(err, {isLogoutOnInvalidAuth}, isPrimaryResource);
    throw err;
  }
}


/** A write to the record's URL is a save, not a load, and must never navigate away. */
function recordLoadUrlOf(options: any) {
  const isRead = options?.method === 'GET';
  return isRead ? options?.url : undefined;
}


/**
 * Turns an auth/ownership failure into the right navigation. Always rethrows via
 * the caller, so a view that wants to handle the error itself still can.
 */
export async function handleResponseStatusError(
  err: any,
  {isLogoutOnInvalidAuth}: Pick<RequestSideEffectsMoreOptions, 'isLogoutOnInvalidAuth'> = {},
  isRecordLoad = false
) {
  const status = err?.response?.status;

  if (status === 401) {
    await resolveAuthFailure({ isRecordLoad, isLogoutOnInvalidAuth });
    return;
  }

  if (isRecordLoad && (status === 403 || status === 404)) {
    redirectToNotFound();
  }
}


/**
 * The API answers both "session dead" and "not your record" with a 401 (or the
 * /Account/LogOn redirect), so ask the server which one it is before acting.
 * A live session means the record isn't ours: not-found on a record load, and
 * otherwise leave it to the caller, never ending a working session mid-edit.
 */
async function resolveAuthFailure(
  {isRecordLoad, isLogoutOnInvalidAuth}: {isRecordLoad: boolean, isLogoutOnInvalidAuth?: boolean}
) {
  if (await isSessionAlive()) {
    if (isRecordLoad) redirectToNotFound();
    return;
  }
  if (isLogoutOnInvalidAuth) {
    // Awaited so the caller's error handling runs after the move to /login, not over the page.
    await useUserStore().logout({ keepRedirect: true });
  }
}


let sessionCheck: Promise<boolean> | undefined;

/** One probe per burst: N parallel auth failures cost one request. */
function isSessionAlive(): Promise<boolean> {
  sessionCheck ??= probeSession().finally(() => { sessionCheck = undefined });
  return sessionCheck;
}

async function probeSession() {
  const { activeDataSourceBaseUrl } = useDataSourceStore();
  const url = new URL( joinPaths(activeDataSourceBaseUrl || "", 'Api/Auth/RefreshToken') ).toString();
  try {
    const response = await fetchResponse({ url, method: 'POST', data: {} });
    return !isLogOnRedirect(response);
  } catch (err: any) {
    // Only an auth answer proves the session is dead. On a network error or 5xx,
    // don't log out on a guess.
    return err?.response?.status !== 401;
  }
}

function isLogOnRedirect(response: any) {
  return /\/Account\/LogOn/.test(response?.request?.responseURL ?? '');
}


/**
 * True when this request IS the record the current route exists to show, so a
 * failure means the page has nothing to render and should become not-found.
 *
 * Derived from the route rather than flagged at each call site, so the knowledge
 * stays in one place.
 */
export function isPrimaryResourceRequest(url?: string) {
  if (!url) return false;

  const recordId = routeRecordId();
  const resource = routeResourceName();
  if (!recordId || !resource) return false;

  const segments = pathSegmentsOf(url);
  const isAboutThisResource = segments.includes(resource);
  const addressesThisRecord = segments[segments.length - 1] === recordId;
  return isAboutThisResource && addressesThisRecord;
}


function routeRecordId() {
  const id = routeInProgress().params?.id;
  return id ? String(id) : undefined;
}


/** meta.resource where the API spells the resource differently from the route. */
function routeResourceName() {
  const { meta, path } = routeInProgress();
  return (meta?.resource as string | undefined) ?? path.split('/')[1];
}


function pathSegmentsOf(url: string) {
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    pathname = url;   // already relative
  }
  return pathname.split('/').filter(Boolean);
}


/**
 * A record owned by another account can come back 200: empty list,
 * {success:false}, or a legacy view page with no record. Rejected here so the
 * caller never unwraps [0] into undefined.
 */
function assertRecordFound(responseBody: any, isRecordLoad: boolean) {
  if (!isRecordLoad) return;

  const hasNoBody = responseBody == null;
  const isEmptyList = Array.isArray(responseBody) && responseBody.length === 0;
  const reportsFailure = responseBody?.success === false;
  const isViewPageWithoutRecord = typeof responseBody === 'string' && !viewCodeHasRecord(responseBody);

  if (!(hasNoBody || isEmptyList || reportsFailure || isViewPageWithoutRecord)) return;
  redirectToNotFound();
  throw {name: RECORD_NOT_FOUND_ERROR, message: 'The requested record is not available.'};
}


/** Remembers the list page to offer as a way back, and replaces so Back doesn't retry the dead record. */
export function redirectToNotFound() {
  const { name, fullPath } = router.currentRoute.value;
  if (name === 'not-found') return;
  // A logout got there first: not-found would only bounce to a bare /login and drop the redirect.
  if (name === 'login' || !useUserStore().isLoggedIn) return;
  router.replace({ name: 'not-found', query: { from: listPathFor(fullPath) } });
}


/** '/players/edit/12' -> '/players'. Falls back to home for anything else. */
function listPathFor(fullPath: string) {
  const listPath = fullPath.split('?')[0].split('/edit/')[0];
  return listPath && listPath !== fullPath.split('?')[0] ? listPath : '/';
}


async function fetchResponseAndHandleAuth(
  options,
  {isLogoutOnInvalidAuth = false, isPrimaryResource = false}
) {
  let response;
  if (isLogoutOnInvalidAuth) {
    // /Account/LogOn means both "not yours" and "not signed in": resolved like a 401.
    const onInvalidAuth = () => resolveAuthFailure({ isRecordLoad: isPrimaryResource, isLogoutOnInvalidAuth: true });
    response = await runAndHandleInvalidAuth(() => fetchResponse(options), onInvalidAuth)
  } else {
    response = await fetchResponse(options)
  }
  return response;
}

async function fetchResponse(options) {
  const response = await axios({...options, withCredentials: true});
  return response;
}